<!--
  CWS-1778: the cancel & refund modal, as drawn in the Orders Refund Flow
  design: item card → (reason) → refund tiles → summary → Keep order / Confirm.
  Every amount is server-priced; the panel formats and forwards.
-->
<div class="modal-overlay" *ngIf="isOpen" (click)="close()">
  <div
    class="crp"
    role="dialog"
    aria-modal="true"
    aria-labelledby="crp-title"
    [attr.data-state]="state"
    (click)="$event.stopPropagation()"
  >
    <header class="crp__header">
      <div class="crp__heading">
        <h2 class="crp__title" id="crp-title">{{ title }}</h2>
        <p class="crp__subtitle" *ngIf="subtitle">{{ subtitle }}</p>
      </div>
      <button
        type="button"
        class="crp__close"
        aria-label="Close"
        [disabled]="state === 'processing'"
        (click)="close()"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M1 1L11 11M1 11L11 1"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </header>

    <div class="crp__body">
      <p class="crp__loading" *ngIf="state === 'loading'" role="status">Loading…</p>

      <div class="crp__alert crp__alert--error" *ngIf="state === 'unavailable'" role="alert">
        {{ errorMessage }}
      </div>

      <ng-container *ngIf="(state === 'select' || state === 'processing') && options">
        <div class="crp__alert crp__alert--error" *ngIf="errorMessage" role="alert">
          {{ errorMessage }}
        </div>

        <!--
          CWS-1947: how long this cancel stays valid. The order auto-completes a
          minute after payment, and without this the operator has no way of
          knowing their Confirm is about to stop being honoured.
        -->
        <div
          class="crp__window"
          [class.crp__window--urgent]="holdRunningOut"
          *ngIf="showHoldCountdown"
          role="status"
          aria-live="polite"
        >
          <span class="crp__window-label">Cancellation window</span>
          <span class="crp__window-time">{{ holdCountdown }}</span>
        </div>

        <!-- Completed: the design's blue note -->
        <div class="crp__note" *ngIf="completedNote as note">
          <svg
            class="crp__note-icon"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.2" />
            <path
              d="M7 6.2V10M7 4.2V4.4"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
            />
          </svg>
          <span>{{ note }}</span>
        </div>

        <!-- CWS-1579: a family primary — Cancel all / Cancel primary only, a refund per plate -->
        <ng-container *ngIf="family">
          <div class="crp__field">
            <span class="crp__label">Family plan</span>
            <div class="crp__tiles" role="radiogroup" aria-label="Family plan">
              <label
                class="crp__tile"
                *ngFor="let tile of familyModeTiles; trackBy: trackTile"
                [class.crp__tile--selected]="familySelection.mode === tile.mode"
                [class.crp__tile--disabled]="tile.disabled"
              >
                <input
                  type="radio"
                  class="crp__radio"
                  name="crp-family-mode"
                  [value]="tile.mode"
                  [checked]="familySelection.mode === tile.mode"
                  [disabled]="inputsLocked || tile.disabled"
                  (change)="pickFamilyMode(tile.mode)"
                />
                <span class="crp__tile-copy">
                  <span class="crp__tile-label">{{ tile.label }}</span>
                  <span class="crp__tile-sub">{{ tile.sub }}</span>
                </span>
              </label>
            </div>
          </div>

          <div class="crp__field" *ngIf="needsNewPrimary">
            <label class="crp__label" for="crp-new-primary">New primary</label>
            <select
              id="crp-new-primary"
              class="crp__select"
              [ngModel]="familySelection.newPrimaryMemberId"
              (ngModelChange)="pickNewPrimary($event)"
              [disabled]="inputsLocked"
            >
              <option [ngValue]="null">Select a member</option>
              <option
                *ngFor="let option of newPrimaryOptions; trackBy: trackNewPrimary"
                [ngValue]="option.plate.member_id"
                [disabled]="option.disabled"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <p class="crp__no-refund" *ngIf="primaryOnlyNote as note">{{ note }}</p>

          <div
            class="crp__item crp__item--with-when"
            *ngFor="let plate of familyPlates; trackBy: trackPlate"
          >
            <div class="crp__item-head">
              <div class="crp__item-copy">
                <span class="crp__item-title">
                  <span class="crp__item-label">{{ plateLabel(plate) }}</span>
                  <span class="crp__chip crp__chip--live">{{ plateRole(plate) }}</span>
                </span>
                <span class="crp__item-sub">{{ plateRefundText(plate) }}</span>
              </div>
            </div>
            <div class="crp__when">
              <label class="crp__when-label" [attr.for]="'crp-refund-' + plate.subscription_id"
                >Refund</label
              >
              <select
                class="crp__select"
                [id]="'crp-refund-' + plate.subscription_id"
                [ngModel]="plateOption(plate)"
                (ngModelChange)="pickPlateOption(plate, $event)"
                [disabled]="inputsLocked"
              >
                <option
                  *ngFor="let entry of plateOptions(plate); trackBy: trackPlateOption"
                  [ngValue]="entry.option"
                >
                  {{ entry.label }}
                </option>
              </select>
              <div class="crp__custom" *ngIf="plateIsCustom(plate)">
                <div class="crp__custom-input-wrap">
                  <span class="crp__custom-prefix" aria-hidden="true">$</span>
                  <input
                    type="text"
                    inputmode="decimal"
                    class="crp__custom-input"
                    placeholder="0.00"
                    [attr.aria-label]="'Custom refund for ' + plateLabel(plate)"
                    [ngModel]="plateCustomInput(plate)"
                    (ngModelChange)="setPlateCustomInput(plate, $event)"
                    [disabled]="inputsLocked"
                  />
                </div>
                <span
                  class="crp__custom-hint crp__custom-hint--problem"
                  *ngIf="plateProblem(plate) as problem"
                  >{{ problem }}</span
                >
              </div>
            </div>
          </div>
          <p class="crp__no-refund" *ngIf="familyEndingNote as note">{{ note }}</p>

          <div class="crp__item" *ngIf="showWhen">
            <ng-container [ngTemplateOutlet]="whenTiles"></ng-container>
          </div>
        </ng-container>

        <!-- Item card -->
        <div class="crp__item" [class.crp__item--with-when]="isMembership" *ngIf="!family">
          <div class="crp__item-head">
            <div class="crp__item-copy">
              <span class="crp__item-title">
                <span class="crp__item-label">{{ label }}</span>
                <span
                  class="crp__chip"
                  [class.crp__chip--done]="isRefund"
                  [class.crp__chip--live]="!isRefund"
                  >{{ stateLabel }}</span
                >
              </span>
              <span class="crp__item-sub" *ngFor="let line of itemSublines">{{ line }}</span>
            </div>
            <span class="crp__item-amount">{{ itemAmount }}</span>
          </div>
          <!-- Membership: when it ends -->
          <ng-container *ngIf="showWhen" [ngTemplateOutlet]="whenTiles"></ng-container>
        </div>

        <!-- Reason -->
        <div class="crp__field" *ngIf="reasonPrompt && (!family || familySelection.mode)">
          <label class="crp__label" for="crp-reason">{{ reasonLabel }}</label>
          <select
            id="crp-reason"
            class="crp__select"
            [ngModel]="selection.reasonCode"
            (ngModelChange)="pickReason($event)"
            [disabled]="inputsLocked"
          >
            <option [ngValue]="null">Select a reason</option>
            <option *ngFor="let reason of reasons; trackBy: trackReason" [ngValue]="reason.code">
              {{ reason.label }}
            </option>
          </select>
          <div class="crp__note-field" *ngIf="noteRequired || selection.reasonNote">
            <textarea
              class="crp__textarea"
              rows="2"
              [maxlength]="noteMax"
              [(ngModel)]="selection.reasonNote"
              [disabled]="inputsLocked"
              [placeholder]="noteRequired ? 'What happened? (required)' : 'Note (optional)'"
              aria-label="Reason note"
            ></textarea>
          </div>
        </div>

        <!-- Refund -->
        <div class="crp__field" *ngIf="!family && refundOptions.length">
          <span class="crp__label">Refund</span>
          <div class="crp__tiles" role="radiogroup" aria-label="Refund">
            <label
              class="crp__tile"
              *ngFor="let option of refundOptions"
              [class.crp__tile--selected]="selection.choice.option === option"
            >
              <input
                type="radio"
                class="crp__radio"
                name="crp-refund"
                [value]="option"
                [checked]="selection.choice.option === option"
                [disabled]="inputsLocked"
                (change)="pickOption(option)"
              />
              <span class="crp__tile-copy">
                <span class="crp__tile-label">{{ optionLabels[option] }}</span>
                <span class="crp__tile-sub" *ngIf="optionAmount(option) as amount">{{
                  amount
                }}</span>
              </span>
            </label>
          </div>

          <div class="crp__custom" *ngIf="selection.choice.option === 'custom'">
            <div class="crp__custom-input-wrap">
              <span class="crp__custom-prefix" aria-hidden="true">$</span>
              <input
                type="text"
                inputmode="decimal"
                class="crp__custom-input"
                placeholder="0.00"
                aria-label="Custom refund amount"
                [ngModel]="selection.choice.customInput"
                (ngModelChange)="setCustomInput($event)"
                [disabled]="inputsLocked"
              />
            </div>
            <span class="crp__custom-hint" [class.crp__custom-hint--problem]="customProblem">{{
              customProblem || customMaxLabel
            }}</span>
          </div>
        </div>
        <p class="crp__no-refund" *ngIf="!family && !refundOptions.length">
          No refund — nothing was paid for this item.
        </p>

        <!-- Summary -->
        <div class="crp__summary" *ngIf="family && familySelection.mode">
          <div class="crp__summary-row" *ngFor="let plate of familyPlates; trackBy: trackPlate">
            <span class="crp__summary-item">{{ plateLabel(plate) }}</span>
            <span class="crp__summary-outcome">{{ plateRefundText(plate) }}</span>
          </div>
          <div class="crp__summary-row crp__summary-row--total">
            <span>Total refund</span>
            <span>{{ money(refundCents) }}</span>
          </div>
        </div>

        <div class="crp__summary" *ngIf="!family">
          <div class="crp__summary-row">
            <span class="crp__summary-item">{{ label }}</span>
            <span class="crp__summary-outcome">{{ summaryOutcome }}</span>
          </div>
          <div class="crp__summary-row crp__summary-row--total">
            <span>{{ summaryTotalLabel }}</span>
            <span>{{ money(refundCents) }}</span>
          </div>
        </div>

        <label class="crp__cash" *ngIf="isCash && refundCents > 0">
          <input
            type="checkbox"
            class="crp__check"
            [(ngModel)]="selection.cashHandedOver"
            [disabled]="inputsLocked"
          />
          <span>I have handed {{ money(refundCents) }} back to the customer in cash</span>
        </label>
      </ng-container>
    </div>

    <footer class="crp__footer" *ngIf="state !== 'loading'">
      <ng-container *ngIf="state === 'select' || state === 'processing'">
        <button
          type="button"
          class="crp__btn crp__btn--ghost"
          [disabled]="state === 'processing'"
          (click)="close()"
        >
          Keep order
        </button>
        <button
          type="button"
          class="crp__btn crp__btn--primary"
          [disabled]="!validation.ok || state === 'processing'"
          [attr.title]="validation.ok ? null : validation.reason"
          (click)="confirm()"
        >
          {{ state === 'processing' ? 'Working…' : confirmLabel }}
        </button>
      </ng-container>
      <button
        type="button"
        class="crp__btn crp__btn--ghost"
        *ngIf="state === 'unavailable'"
        (click)="close()"
      >
        Close
      </button>
    </footer>
  </div>
</div>

<ng-template #whenTiles>
  <div class="crp__when">
    <span class="crp__when-label">When</span>
    <div class="crp__when-tiles" role="radiogroup" aria-label="When">
      <label
        class="crp__tile"
        [class.crp__tile--selected]="selection.cancelTiming === 'period_end'"
      >
        <input
          type="radio"
          class="crp__radio"
          name="crp-when"
          value="period_end"
          [checked]="selection.cancelTiming === 'period_end'"
          [disabled]="inputsLocked"
          (change)="pickTiming('period_end')"
        />
        <span class="crp__tile-copy">
          <span class="crp__tile-label">At end of billing period</span>
          <span class="crp__tile-sub">{{ periodEndNote }}</span>
        </span>
      </label>
      <label class="crp__tile" [class.crp__tile--selected]="selection.cancelTiming === 'immediate'">
        <input
          type="radio"
          class="crp__radio"
          name="crp-when"
          value="immediate"
          [checked]="selection.cancelTiming === 'immediate'"
          [disabled]="inputsLocked"
          (change)="pickTiming('immediate')"
        />
        <span class="crp__tile-copy">
          <span class="crp__tile-label">Immediately</span>
          <span class="crp__tile-sub">{{ immediateNote }}</span>
        </span>
      </label>
    </div>
  </div>
</ng-template>
